import { useLocation, useSearchParams as useSearchParamsNative, type NavigateOptions } from "react-router";
import { useEffect, useState } from "react";
import { logDebug } from "~/lib/logger";
import type { Nullable, Optional } from "~/lib/types/utils";
import { z } from "zod";

const searchParamsSchema = z.object({
  redirectTo: z.string().optional(),
  "responses.responseId": z.string().optional(),
  "responses.search": z.string().optional(),
  // "responses.sectionIds": z.array(z.string()).optional(),
  // "responses.activeForm": z
  //   .enum([
  //     initialPresentationForm.sectionId,
  //     confirmatoryDiagnosisForm.sectionId,
  //     treatmentEarlyStageForm.sectionId,
  //     treatmentLateStageForm.sectionId,
  //     ongoingManagementForm.sectionId,
  //   ])
  //   .optional(),
  "responses.userIds": z.array(z.string()).optional(),

  //
  // for easy login with test accounts.
  //
  "login.e": z.string().optional(),
  "login.ac": z.string().optional(),
});

export type SearchParams = z.infer<typeof searchParamsSchema>;

function parseSearchParams(searchParams: URLSearchParams) {
  const entries: Record<string, string | string[]> = Object.fromEntries(searchParams.entries());

  // check if value for key is supposed to be an array, if so, parse it as an array
  Object.entries(entries).forEach(([key, value]) => {
    if (!Object.keys(searchParamsSchema.shape).includes(key)) {
      console.log("key", key, "not found in schema");
      return;
    }

    const shapeUnwrapped = searchParamsSchema.shape[key as keyof SearchParams].unwrap();

    // console.log("shapeUnwrapped", shapeUnwrapped);
    if (shapeUnwrapped instanceof z.ZodArray) {
      // parse the value from url encoded string to an array
      const nonUrlEncodedStr = decodeURIComponent(String(value));

      // if the value has commas, split it into an array
      const nonUrlEncodedValue = nonUrlEncodedStr.includes(",") ? nonUrlEncodedStr.split(",") : nonUrlEncodedStr;

      entries[key] = Array.isArray(nonUrlEncodedValue) ? nonUrlEncodedValue : [nonUrlEncodedValue];
    }

    // if (shapeUnwrapped instanceof z.ZodObject) {
    //   // parse the value from url encoded string to an object
    //   const nonUrlEncodedStr = decodeURIComponent(String(value));
    //   console.log("nonUrlEncodedStr", nonUrlEncodedStr);
    //   entries[key] = JSON.parse(nonUrlEncodedStr);
    // }
  });

  return searchParamsSchema.parse(entries);
}

export function stringifySearchParams(searchParams: SearchParams | null) {
  if (searchParams === null) {
    return new URLSearchParams().toString();
  }

  const entries = Object.fromEntries(Object.entries(searchParams).map(([key, value]) => [key, String(value)]));

  return new URLSearchParams(entries).toString();
}

export function useSearchParams() {
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParamsNative();
  const [searchParamsObj, setSearchParamsObj] = useState<SearchParams | null>(null);

  useEffect(() => {
    setSearchParamsObj(parseSearchParams(new URLSearchParams(location.search)));
  }, [location.search]);

  //
  // update the search params object
  //
  function updateSearchParams(params: Optional<Nullable<SearchParams>>, options: NavigateOptions = {}) {
    // loop through the params and remove null values
    const updatedSearchParams = { ...(searchParamsObj ?? {}), ...(params ?? {}) };

    // loop through the params and remove null values
    const filteredParams = Object.fromEntries(
      Object.entries(updatedSearchParams).filter(([_, value]) => value !== null)
    );

    setSearchParamsObj(filteredParams);
    setSearchParams(stringifySearchParams(filteredParams), { preventScrollReset: true, ...options });
  }

  //
  // toggle a search param
  //
  function toggleSearchParam(key: keyof SearchParams, value: string, options: NavigateOptions = {}) {
    const currentValue = searchParamsObj?.[key];
    if (Array.isArray(currentValue)) {
      const newValues = currentValue.includes(value)
        ? currentValue.filter((v) => v !== value)
        : [...currentValue, value];

      updateSearchParams({ [key]: newValues }, options);
    } else {
      updateSearchParams({ [key]: value }, options);
    }
  }

  return { searchParams, searchParamsObj, updateSearchParams, toggleSearchParam };
}
